import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { dt } from "@/lib/fmt";
import { H, Tabs, Table, Panel } from "./ui";

function Reviews() {
  const [rows, setRows] = useState([]);
  const [resp, setResp] = useState({});
  const load = () => api.get("/admin/reviews").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => { try { await api.put(`/admin/reviews/${id}`, body); toast.success("Avaliação atualizada"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!rows.length) return <p className="text-na-ink2" data-testid="no-reviews">Nenhuma avaliação ainda. Só compradores com pedido entregue podem avaliar.</p>;
  return rows.map((r) => (
    <div key={r.id} className="card-na mb-3 p-5 text-sm" data-testid={`review-${r.id}`}>
      <div className="flex items-center gap-2"><b>{r.produto}</b><span className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.nota ? "fill-na-gold text-na-gold" : "text-na-line"}`} />)}</span><span className="text-xs text-na-ink2">{r.cliente_nome} · {dt(r.criado_em)} · {r.aprovado ? "Publicada" : "Pendente"}</span></div>
      <p className="mt-2">{r.texto}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button className="btn-na px-4 py-1.5" onClick={() => patch(r.id, { aprovado: !r.aprovado })} data-testid={`review-approve-${r.id}`}>{r.aprovado ? "Despublicar" : "Aprovar"}</button>
        <input aria-label="Resposta" className="input-na flex-1 py-1.5" defaultValue={r.resposta || ""} onChange={(e) => setResp({ ...resp, [r.id]: e.target.value })} placeholder="Responder (a nota não pode ser editada)" />
        <button className="btn-line px-4 py-1.5" onClick={() => patch(r.id, { resposta: resp[r.id] })}>Responder</button>
      </div>
    </div>
  ));
}

function Returns() {
  const [rows, setRows] = useState([]);
  const [e, setE] = useState(null);
  const load = () => api.get("/admin/returns").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.put(`/admin/returns/${e.id}`, { status: e.status, codigo_reverso: e.codigo_reverso, reembolso: e.reembolso ? Number(e.reembolso) : null }); toast.success("Atualizado"); setE(null); load(); } catch (er) { toast.error(errMsg(er)); } };
  return (
    <>
      <Table rows={rows} onRow={(r) => setE({ ...r })} testid="returns-table" cols={[["numero", "Pedido"], ["cliente", "Cliente", (r) => r.cliente?.nome], ["tipo", "Tipo"], ["motivo", "Motivo"], ["status", "Status"], ["criado_em", "Data", (r) => dt(r.criado_em)]]} />
      <Panel open={!!e} onClose={() => setE(null)} title={`Troca/devolução ${e?.numero}`}>
        {e && <div className="space-y-3 text-sm" data-testid="return-panel">
          <p>{e.motivo}: {e.descricao}</p>
          <select aria-label="Status" className="input-na" value={e.status} onChange={(ev) => setE({ ...e, status: ev.target.value })} data-testid="return-status">{["solicitada", "aprovada", "recusada", "em_transito", "recebida", "reembolsada"].map((s) => <option key={s}>{s}</option>)}</select>
          <input aria-label="Código de logística reversa" className="input-na" placeholder="Código de postagem reversa" value={e.codigo_reverso || ""} onChange={(ev) => setE({ ...e, codigo_reverso: ev.target.value })} />
          <input aria-label="Valor do reembolso" type="number" className="input-na" placeholder="Valor do reembolso" value={e.reembolso || ""} onChange={(ev) => setE({ ...e, reembolso: ev.target.value })} />
          <p className="text-xs text-na-ink2">Status "reembolsada" marca o pedido como reembolsado e devolve o estoque (financeiro ou dono).</p>
          <button className="btn-na" onClick={save} data-testid="return-save">Salvar</button>
        </div>}
      </Panel>
    </>
  );
}

function Adverse() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/adverse_events").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const mark = async (r) => { const protocolo = window.prompt("Protocolo do Notivisa"); if (protocolo === null) return; await api.put(`/admin/adverse_events/${r.id}`, { encaminhado_notivisa: true, protocolo }); load(); };
  return (
    <>
      <p className="mb-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">Ao receber relato de efeito adverso, oriente o cliente a procurar um profissional de saúde e notifique a ANVISA pelo <a className="underline" href="https://www8.anvisa.gov.br/notivisa/frmlogin.asp" target="_blank" rel="noreferrer">Notivisa</a>.</p>
      <Table rows={rows} testid="adverse-table" cols={[["numero", "Pedido"], ["descricao", "Descrição"], ["em", "Data", (r) => dt(r.em)], ["encaminhado_notivisa", "Notivisa", (r) => (r.encaminhado_notivisa ? `Sim ${r.protocolo || ""}` : <button className="btn-line px-3 py-1 text-xs" onClick={() => mark(r)}>Marcar notificado</button>)]]} />
    </>
  );
}

export default function Care() {
  const [tab, setTab] = useState("avaliacoes");
  return (
    <div data-testid="admin-care">
      <H>Avaliações e trocas</H>
      <Tabs value={tab} onChange={setTab} tabs={[["avaliacoes", "Avaliações"], ["trocas", "Trocas e devoluções"], ["adversos", "Efeitos adversos"]]} />
      {tab === "avaliacoes" && <Reviews />}
      {tab === "trocas" && <Returns />}
      {tab === "adversos" && <Adverse />}
    </div>
  );
}
